import { supabase } from "@/lib/supabaseClient";
import { fetchCurrentProfile } from "@/services/auth";

// ---------------------------------------------------------------------------
// Grupos por tema (los crea el administrador; todos los miembros los ven)
// ---------------------------------------------------------------------------

export type CommunityGroup = {
  id: string;
  title: string;
  description: string;
  icon: string;
  active: boolean;
};

export async function fetchGroups(includeInactive = false): Promise<CommunityGroup[]> {
  let query = supabase
    .from("community_groups")
    .select("id, title, description, icon, active")
    .order("created_at", { ascending: true });
  if (!includeInactive) query = query.eq("active", true);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as CommunityGroup[];
}

export async function createGroup(input: { title: string; description: string; icon: string }): Promise<void> {
  const profile = await fetchCurrentProfile();
  if (!profile) throw new Error("Debes iniciar sesión.");
  const { error } = await supabase.from("community_groups").insert({
    organization_id: profile.organizationId,
    title: input.title,
    description: input.description,
    icon: input.icon,
  });
  if (error) throw error;
}

export async function updateGroup(
  id: string,
  patch: Partial<{ title: string; description: string; icon: string; active: boolean }>
): Promise<void> {
  const { error } = await supabase.from("community_groups").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteGroup(id: string): Promise<void> {
  const { error } = await supabase.from("community_groups").delete().eq("id", id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Feed
// ---------------------------------------------------------------------------

export type CommunityComment = {
  id: string;
  postId: string;
  authorId: string;
  authorName: string;
  authorAvatar: string | null;
  content: string;
  createdAt: string;
};

export type CommunityPost = {
  id: string;
  groupId: string | null;
  authorId: string;
  authorName: string;
  authorAvatar: string | null;
  content: string;
  imageUrl: string | null;
  createdAt: string;
  likes: number;
  likedByMe: boolean;
  comments: CommunityComment[];
};

/** "all" = todo el feed, "general" = sin grupo, o el id de un grupo. */
export type FeedFilter = "all" | "general" | string;

export async function fetchPosts(filter: FeedFilter, limit = 40): Promise<CommunityPost[]> {
  const { data: userData } = await supabase.auth.getUser();
  const me = userData.user?.id ?? null;

  let query = supabase
    .from("community_posts")
    .select("id, group_id, author_id, content, image_url, created_at")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (filter === "general") query = query.is("group_id", null);
  else if (filter !== "all") query = query.eq("group_id", filter);

  const { data: posts, error } = await query;
  if (error) throw error;
  const rows = posts ?? [];
  if (rows.length === 0) return [];

  const postIds = rows.map((p) => p.id as string);
  const [commentsRes, reactionsRes] = await Promise.all([
    supabase
      .from("community_comments")
      .select("id, post_id, author_id, content, created_at")
      .in("post_id", postIds)
      .order("created_at", { ascending: true }),
    supabase.from("community_reactions").select("post_id, user_id").in("post_id", postIds),
  ]);
  if (commentsRes.error) throw commentsRes.error;
  if (reactionsRes.error) throw reactionsRes.error;
  const comments = commentsRes.data ?? [];
  const reactions = reactionsRes.data ?? [];

  const authorIds = Array.from(
    new Set([...rows.map((p) => p.author_id as string), ...comments.map((c) => c.author_id as string)])
  );
  const { data: authors } = await supabase.from("profiles").select("id, full_name, avatar_url").in("id", authorIds);
  const authorById = new Map(
    (authors ?? []).map((a) => [a.id as string, { name: (a.full_name as string | null) ?? "Miembro", avatar: a.avatar_url as string | null }])
  );

  return rows.map((p) => {
    const author = authorById.get(p.author_id as string);
    const postReactions = reactions.filter((r) => r.post_id === p.id);
    return {
      id: p.id as string,
      groupId: p.group_id as string | null,
      authorId: p.author_id as string,
      authorName: author?.name ?? "Miembro",
      authorAvatar: author?.avatar ?? null,
      content: p.content as string,
      imageUrl: (p.image_url as string | null) ?? null,
      createdAt: p.created_at as string,
      likes: postReactions.length,
      likedByMe: me ? postReactions.some((r) => r.user_id === me) : false,
      comments: comments
        .filter((c) => c.post_id === p.id)
        .map((c) => {
          const a = authorById.get(c.author_id as string);
          return {
            id: c.id as string,
            postId: c.post_id as string,
            authorId: c.author_id as string,
            authorName: a?.name ?? "Miembro",
            authorAvatar: a?.avatar ?? null,
            content: c.content as string,
            createdAt: c.created_at as string,
          };
        }),
    };
  });
}

function normalize(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/**
 * Cumplimiento: revisa el texto contra las frases PROHIBIDAS que el
 * administrador mantiene en el Panel Administrador. Si la consulta falla,
 * no bloquea la publicación (la regla es una ayuda, no un punto único de falla).
 */
export async function findProhibitedTerms(text: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("compliance_terms")
    .select("phrase")
    .eq("active", true)
    .eq("category", "prohibida");
  if (error || !data) return [];
  const n = normalize(text);
  return data.map((t) => t.phrase as string).filter((phrase) => n.includes(normalize(phrase)));
}

export class ProhibitedContentError extends Error {
  terms: string[];
  constructor(terms: string[]) {
    super("prohibited_content");
    this.terms = terms;
  }
}

export async function createPost(input: { content: string; groupId: string | null }): Promise<void> {
  const profile = await fetchCurrentProfile();
  if (!profile) throw new Error("Debes iniciar sesión.");
  const content = input.content.trim();
  const violations = await findProhibitedTerms(content);
  if (violations.length > 0) throw new ProhibitedContentError(violations);
  const { error } = await supabase.from("community_posts").insert({
    organization_id: profile.organizationId,
    group_id: input.groupId,
    content,
  });
  if (error) throw error;
}

export async function deletePost(id: string): Promise<void> {
  const { error } = await supabase.from("community_posts").delete().eq("id", id);
  if (error) throw error;
}

export async function addComment(postId: string, content: string): Promise<void> {
  const profile = await fetchCurrentProfile();
  if (!profile) throw new Error("Debes iniciar sesión.");
  const clean = content.trim();
  const violations = await findProhibitedTerms(clean);
  if (violations.length > 0) throw new ProhibitedContentError(violations);
  const { error } = await supabase.from("community_comments").insert({
    organization_id: profile.organizationId,
    post_id: postId,
    content: clean,
  });
  if (error) throw error;
}

export async function deleteComment(id: string): Promise<void> {
  const { error } = await supabase.from("community_comments").delete().eq("id", id);
  if (error) throw error;
}

export async function setReaction(postId: string, liked: boolean): Promise<void> {
  if (liked) {
    const profile = await fetchCurrentProfile();
    if (!profile) throw new Error("Debes iniciar sesión.");
    const { error } = await supabase
      .from("community_reactions")
      .insert({ post_id: postId, organization_id: profile.organizationId });
    if (error && error.code !== "23505") throw error;
  } else {
    const { data: userData } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("community_reactions")
      .delete()
      .eq("post_id", postId)
      .eq("user_id", userData.user?.id ?? "");
    if (error) throw error;
  }
}
